import { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, Button, Input, useToast } from '@/components/common';
import { ProfileSyncTester } from '@/components/debug/ProfileSyncTester';
import { cvService } from '@/services/cv.service';
import {
  ExperienceEditor,
  EducationEditor,
  SkillsManager,
  type Experience,
  type Education,
} from '@/components/profile';
import {
  useProfileStore,
  type ProfileExperience,
  type ProfileEducation,
} from '@/store/profile.store';
import { profileService } from '@/services/profile.service';
import { useAuth } from '@/hooks';
import { BillingSection } from '@/components/billing/BillingSection';

const personalInfoSchemaCloud = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email'),
  phone: z.string().optional(),
  location: z.string().optional(),
  summary: z.string().optional(),
});

const personalInfoSchemaLocal = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().optional(),
  phone: z.string().optional(),
  location: z.string().optional(),
  summary: z.string().optional(),
});

// Union type that satisfies both schemas
type PersonalInfoFormData = {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  location?: string;
  summary?: string;
};

type EditSection = 'personal' | 'experience' | 'education' | 'skills' | 'certifications' | null;

export function ProfilePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { mode } = useAuth();
  const { profile, setProfile, setLoading, setError } = useProfileStore();

  const [editingSection, setEditingSection] = useState<EditSection>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isReUploading, setIsReUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleReUploadCV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsReUploading(true);
    try {
      const parsed = await cvService.parseCV(file);

      // Update profile with parsed data
      const fullName = (parsed.personalInfo as any)?.fullName ||
        `${parsed.personalInfo?.firstName || ''} ${parsed.personalInfo?.lastName || ''}`.trim();

      const skills = Array.isArray(parsed.skills)
        ? parsed.skills.filter((s: any) => typeof s === 'string')
        : [];

      const updatedProfile = {
        firstName: parsed.personalInfo?.firstName || fullName.split(/\s+/)[0] || profile?.firstName || '',
        lastName: parsed.personalInfo?.lastName || fullName.split(/\s+/).slice(1).join(' ') || profile?.lastName || '',
        email: parsed.personalInfo?.email || profile?.email || '',
        phone: parsed.personalInfo?.phone || profile?.phone || null,
        location: parsed.personalInfo?.location || profile?.location || null,
        summary: parsed.summary || profile?.summary || null,
        skills,
        experience: parsed.experience?.map((exp: any) => ({
          id: exp.id || `exp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          company: exp.company,
          position: exp.position,
          startDate: exp.startDate,
          endDate: exp.endDate || null,
          description: exp.description || '',
          current: !exp.endDate,
        })) || [],
        education: parsed.education?.map((edu: any) => ({
          id: edu.id || `edu-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          institution: edu.institution,
          degree: edu.degree,
          fieldOfStudy: edu.field || '',
          startDate: edu.startDate,
          endDate: edu.endDate || null,
          current: !edu.endDate,
        })) || [],
        certifications: parsed.certifications?.map((cert: any) => ({
          id: cert.id || `cert-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          name: cert.name || '',
          issuer: cert.issuer || '',
          issueDate: cert.date || cert.issueDate || '',
          expiryDate: cert.expiryDate || null,
          credentialId: cert.credentialId || null,
        })) || [],
      };

      // Update personal info + summary
      await profileService.updateProfile(updatedProfile);

      // Update sections that updateProfile doesn't handle
      if (updatedProfile.experience.length > 0) {
        await profileService.updateExperience(updatedProfile.experience);
      }
      if (updatedProfile.education.length > 0) {
        await profileService.updateEducation(updatedProfile.education);
      }
      if (updatedProfile.skills.length > 0) {
        await profileService.updateSkills(updatedProfile.skills);
      }
      if (updatedProfile.certifications.length > 0) {
        await profileService.updateCertifications(updatedProfile.certifications);
      }

      await loadProfile();
      toast.success('Profile updated from CV!');
    } catch (error) {
      console.error('CV re-upload error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to parse CV. Please try again.');
    } finally {
      setIsReUploading(false);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Local state for editing sections
  const [localExperience, setLocalExperience] = useState<Experience[]>([]);
  const [localEducation, setLocalEducation] = useState<Education[]>([]);
  const [localSkills, setLocalSkills] = useState<string[]>([]);
  const [localCertifications, setLocalCertifications] = useState<Array<{ name: string; issuer: string; date: string }>>([]);

  const normalizedSkills = useMemo(() => {
    if (!profile?.skills) return [];

    // Convert all skills to strings and filter out empty ones
    const skills = profile.skills
      .map((skill: any) => {
        if (typeof skill === 'string') return skill.trim();
        if (skill && typeof skill === 'object') {
          return (skill.name ?? skill.label ?? skill.value ?? '').trim();
        }
        return '';
      })
      .filter(Boolean);

    // Remove duplicates while preserving order
    return Array.from(new Set(skills));
  }, [profile?.skills]);

  const schema = useMemo(
    () => (mode === 'cloud' ? personalInfoSchemaCloud : personalInfoSchemaLocal),
    [mode]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetPersonalInfo,
  } = useForm<PersonalInfoFormData>({
    resolver: zodResolver(schema),
  });

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await profileService.getProfile();
      setProfile(data);
      resetPersonalInfo({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || '',
        location: data.location || '',
        summary: data.summary || '',
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError(error instanceof Error ? error.message : 'Failed to load profile');
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSection = (section: EditSection) => {
    if (!profile) return;

    // Initialize local state for the section being edited
    if (section === 'experience') {
      setLocalExperience(
        profile.experience.map((exp) => ({
          company: exp.company,
          position: exp.position,
          startDate: exp.startDate,
          endDate: exp.endDate || undefined,
          description: exp.description || undefined,
        }))
      );
    } else if (section === 'education') {
      setLocalEducation(
        profile.education.map((edu) => ({
          institution: edu.institution,
          degree: edu.degree,
          field: edu.fieldOfStudy || undefined,
          startDate: edu.startDate,
          endDate: edu.endDate || undefined,
        }))
      );
    } else if (section === 'skills') {
      setLocalSkills([...normalizedSkills]);
    } else if (section === 'certifications') {
      setLocalCertifications(
        (profile.certifications || []).map((cert) => ({
          name: cert.name || '',
          issuer: cert.issuer || '',
          date: cert.issueDate || '',
        }))
      );
    }

    setEditingSection(section);
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setLocalExperience([]);
    setLocalEducation([]);
    setLocalSkills([]);
    setLocalCertifications([]);
  };

  // Save personal info
  const onSavePersonalInfo = async (data: PersonalInfoFormData) => {
    setIsSaving(true);
    try {
      const updated = await profileService.updateProfile(data);
      setProfile(updated);
      setEditingSection(null);
      toast.success('Personal information updated successfully');
    } catch (error) {
      console.error('Failed to update personal info:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Save experience
  const handleSaveExperience = async () => {
    setIsSaving(true);
    try {
      const experience: ProfileExperience[] = localExperience.map((exp, index) => ({
        id: profile?.experience[index]?.id || `temp-${index}`,
        company: exp.company,
        position: exp.position,
        startDate: exp.startDate,
        endDate: exp.endDate || null,
        description: exp.description || '',
        current: !exp.endDate,
      }));

      const updated = await profileService.updateExperience(experience);
      setProfile(updated);
      setEditingSection(null);
      setLocalExperience([]);
      toast.success('Experience updated successfully');
    } catch (error) {
      console.error('Failed to update experience:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update experience');
    } finally {
      setIsSaving(false);
    }
  };

  // Save education
  const handleSaveEducation = async () => {
    setIsSaving(true);
    try {
      const education: ProfileEducation[] = localEducation.map((edu, index) => ({
        id: profile?.education[index]?.id || `temp-${index}`,
        institution: edu.institution,
        degree: edu.degree,
        fieldOfStudy: edu.field || '',
        startDate: edu.startDate,
        endDate: edu.endDate || null,
        current: !edu.endDate,
      }));

      const updated = await profileService.updateEducation(education);
      setProfile(updated);
      setEditingSection(null);
      setLocalEducation([]);
      toast.success('Education updated successfully');
    } catch (error) {
      console.error('Failed to update education:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update education');
    } finally {
      setIsSaving(false);
    }
  };

  // Save skills
  const handleSaveSkills = async () => {
    setIsSaving(true);
    try {
      const updated = await profileService.updateSkills(localSkills);
      setProfile(updated);
      setEditingSection(null);
      setLocalSkills([]);
      toast.success('Skills updated successfully');
    } catch (error) {
      console.error('Failed to update skills:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update skills');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCertifications = async () => {
    setIsSaving(true);
    try {
      const certData = localCertifications.filter(c => c.name.trim());
      const updated = await profileService.updateCertifications(
        certData.map(c => ({
          name: c.name,
          issuer: c.issuer,
          date: c.date,
        })) as any,
      );
      setProfile(updated);
      setEditingSection(null);
      setLocalCertifications([]);
      toast.success('Certifications updated successfully');
    } catch (error) {
      console.error('Failed to update certifications:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update certifications');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete profile (works for both local and cloud modes)
  const handleDeleteProfile = async () => {
    if (!window.confirm('Are you sure you want to delete your profile? This cannot be undone.')) return;

    try {
      setIsSaving(true);
      await profileService.deleteProfile();
      useProfileStore.getState().clearProfile();
      toast.success('Profile deleted');
      navigate('/app/onboarding');
    } catch (error) {
      console.error('Failed to delete profile:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <h1 className="text-2xl font-bold text-text-primary">No Profile Yet</h1>
        <p className="text-text-secondary text-center max-w-md">
          Create your profile by uploading a CV or filling in your details manually.
        </p>
        <Button onClick={() => navigate('/app/onboarding')} size="md">
          Create Profile
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-4 sm:pt-8">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">My Profile</h1>
              <p className="mt-1 text-text-secondary">
                Your resume data used for ATS analysis and CV generation.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                onChange={handleReUploadCV}
                className="hidden"
              />
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                isLoading={isReUploading}
                disabled={isReUploading}
              >
                {isReUploading ? 'Parsing CV...' : 'Update from CV'}
              </Button>
              {/* Back to Pipeline hidden on mobile — BottomNav covers navigation */}
              <Button
                variant="secondary"
                onClick={() => navigate('/app/pipeline')}
                className="hidden sm:inline-flex"
              >
                Back to Pipeline
              </Button>
              {/* Delete — separated visually, mobile shows last */}
              <Button
                variant="danger"
                onClick={handleDeleteProfile}
                isLoading={isSaving}
              >
                Delete Profile
              </Button>
            </div>
          </div>
        </div>

        {/* Debug Component */}
        {process.env.NODE_ENV === 'development' && <ProfileSyncTester />}

        {/* Subscription / Billing */}
        <BillingSection />

        {/* Personal Information */}
        <Card className="mb-4 sm:mb-6">
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-text-primary">Personal Information</h2>
              {editingSection !== 'personal' && (
                <Button size="sm" onClick={() => handleEditSection('personal')}>
                  Edit
                </Button>
              )}
            </div>

            {editingSection === 'personal' ? (
              <form onSubmit={handleSubmit(onSavePersonalInfo)} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="First Name *"
                    error={errors.firstName?.message}
                    fullWidth
                    {...register('firstName')}
                  />
                  <Input
                    label="Last Name *"
                    error={errors.lastName?.message}
                    fullWidth
                    {...register('lastName')}
                  />
                </div>

                <Input
                  label="Email *"
                  type="email"
                  error={errors.email?.message}
                  fullWidth
                  {...register('email')}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Phone"
                    error={errors.phone?.message}
                    fullWidth
                    {...register('phone')}
                  />
                  <Input
                    label="Location"
                    error={errors.location?.message}
                    fullWidth
                    {...register('location')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Professional Summary
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-text-primary"
                    {...register('summary')}
                  />
                  {errors.summary && (
                    <p className="mt-1 text-sm text-red-600">{errors.summary.message}</p>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={isSaving}>
                    Save Changes
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-text-secondary">Name</p>
                    <p className="font-medium text-text-primary">
                      {profile.firstName} {profile.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary">Email</p>
                    <p className="font-medium text-text-primary">{profile.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-text-secondary">Phone</p>
                    <p className="font-medium text-text-primary">
                      {profile.phone || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary">Location</p>
                    <p className="font-medium text-text-primary">
                      {profile.location || 'Not provided'}
                    </p>
                  </div>
                </div>
                {profile.summary && (
                  <div>
                    <p className="text-sm text-text-secondary">Professional Summary</p>
                    <p className="text-text-primary mt-1">{profile.summary}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Experience Section */}
        <Card className="mb-4 sm:mb-6">
          <div className="p-4 sm:p-6">
            {editingSection === 'experience' ? (
              <div>
                <ExperienceEditor experiences={localExperience} onChange={setLocalExperience} />
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveExperience} isLoading={isSaving}>
                    Save Experience
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-text-primary">Work Experience</h2>
                  <Button size="sm" onClick={() => handleEditSection('experience')}>
                    Edit
                  </Button>
                </div>
                {profile.experience.length === 0 ? (
                  <p className="text-text-secondary">No work experience added yet. Click Edit to add your work history.</p>
                ) : (
                  <div className="space-y-4">
                    {profile.experience.map((exp, index) => (
                      <div key={`exp-${index}-${exp.company}-${exp.position}`} className="border-l-2 border-primary pl-4">
                        <h3 className="font-semibold text-text-primary">{exp.position}</h3>
                        <p className="text-text-primary">{exp.company}</p>
                        <p className="text-sm text-text-secondary">
                          {exp.startDate} - {exp.endDate || 'Present'}
                        </p>
                        {exp.description && (
                          <p className="mt-2 text-text-secondary">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Education Section */}
        <Card className="mb-4 sm:mb-6">
          <div className="p-4 sm:p-6">
            {editingSection === 'education' ? (
              <div>
                <EducationEditor education={localEducation} onChange={setLocalEducation} />
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEducation} isLoading={isSaving}>
                    Save Education
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-text-primary">Education</h2>
                  <Button size="sm" onClick={() => handleEditSection('education')}>
                    Edit
                  </Button>
                </div>
                {profile.education.length === 0 ? (
                  <p className="text-text-secondary">No education added yet. Click Edit to add your education.</p>
                ) : (
                  <div className="space-y-4">
                    {profile.education.map((edu, index) => (
                      <div key={`edu-${index}-${edu.institution}-${edu.degree}`} className="border-l-2 border-primary pl-4">
                        <h3 className="font-semibold text-text-primary">{edu.degree}</h3>
                        {edu.fieldOfStudy && (
                          <p className="text-text-primary">{edu.fieldOfStudy}</p>
                        )}
                        <p className="text-text-primary">{edu.institution}</p>
                        <p className="text-sm text-text-secondary">
                          {edu.startDate} - {edu.endDate || 'Present'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Certifications Section */}
        <Card className="mb-4 sm:mb-6">
          <div className="p-4 sm:p-6">
            {editingSection === 'certifications' ? (
              <div>
                <h2 className="text-xl font-semibold text-text-primary mb-4">Certifications</h2>
                <div className="space-y-3">
                  {localCertifications.map((cert, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 border border-border rounded-md bg-surface">
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={cert.name}
                          onChange={(e) => {
                            const updated = [...localCertifications];
                            updated[i] = { ...updated[i], name: e.target.value };
                            setLocalCertifications(updated);
                          }}
                          placeholder="Certification name"
                          className="w-full px-2 py-1 text-sm border border-border rounded bg-background text-text-primary"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={cert.issuer}
                            onChange={(e) => {
                              const updated = [...localCertifications];
                              updated[i] = { ...updated[i], issuer: e.target.value };
                              setLocalCertifications(updated);
                            }}
                            placeholder="Issuer"
                            className="flex-1 px-2 py-1 text-sm border border-border rounded bg-background text-text-primary"
                          />
                          <input
                            type="text"
                            value={cert.date}
                            onChange={(e) => {
                              const updated = [...localCertifications];
                              updated[i] = { ...updated[i], date: e.target.value };
                              setLocalCertifications(updated);
                            }}
                            placeholder="Date"
                            className="w-28 px-2 py-1 text-sm border border-border rounded bg-background text-text-primary"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setLocalCertifications(localCertifications.filter((_, j) => j !== i))}
                        className="flex items-center justify-center w-8 h-8 text-text-secondary hover:text-danger rounded flex-shrink-0"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => setLocalCertifications([...localCertifications, { name: '', issuer: '', date: '' }])}
                >
                  + Add Certification
                </Button>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveCertifications} isLoading={isSaving}>
                    Save Certifications
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-text-primary">Certifications</h2>
                  <Button size="sm" onClick={() => handleEditSection('certifications')}>
                    Edit
                  </Button>
                </div>
                {(profile.certifications || []).length === 0 ? (
                  <p className="text-text-secondary">No certifications added yet. Click Edit to add certifications.</p>
                ) : (
                  <div className="space-y-4">
                    {(profile.certifications || []).map((cert, index) => (
                      <div key={`cert-${index}-${cert.name}`} className="border-l-2 border-primary pl-4">
                        <h3 className="font-semibold text-text-primary">{cert.name}</h3>
                        {cert.issuer && <p className="text-text-secondary">{cert.issuer}</p>}
                        {cert.issueDate && <p className="text-sm text-text-secondary">{cert.issueDate}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Skills Section */}
        <Card className="mb-4 sm:mb-6">
          <div className="p-4 sm:p-6">
            {editingSection === 'skills' ? (
              <div>
                <SkillsManager skills={localSkills} onChange={setLocalSkills} />
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveSkills} isLoading={isSaving}>
                    Save Skills
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-text-primary">Skills</h2>
                  <Button size="sm" onClick={() => handleEditSection('skills')}>
                    Edit
                  </Button>
                </div>
                {normalizedSkills.length === 0 ? (
                  <p className="text-text-secondary">No skills added yet. Click Edit to add your skills.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {normalizedSkills.map((skill, index) => (
                      <span
                        key={`${skill}-${index}`}
                        className="px-3 py-1 rounded-full text-sm bg-primary-soft text-primary border border-primary"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
    </div>
  );
}
